import { ApiConfig, handleResponse } from './config';

export interface MediaFile {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  altText: string;
  tags: string[];
  uploadedBy: string;
  usageCount: number;
  lastUsed?: Date;
  readableSize: string;
  fileExtension: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Helper function to ensure media URLs are absolute
const processMediaFile = (file: any): MediaFile => {
  const processedFile = { ...file };

  // Convert relative URLs to absolute using the configured API base URL
  if (processedFile.url && !processedFile.url.startsWith('http')) {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) {
      // Strip trailing /api from the base URL to get the origin
      const baseOrigin = apiUrl.replace(/\/api\/?$/, '');
      processedFile.url = `${baseOrigin}${processedFile.url}`;
    }
    // In development without VITE_API_URL, keep relative URLs (Vite proxy handles them)
  }

  return processedFile;
};

export interface MediaUploadResponse {
  success: boolean;
  message: string;
  data: MediaFile[];
  errors?: Array<{
    filename: string;
    error: string;
  }>;
}

export interface MediaListResponse {
  success: boolean;
  data: MediaFile[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface MediaSearchResponse {
  success: boolean;
  data: MediaFile[];
  count: number;
}

export interface UploadProgress {
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
}

export interface MediaListParams {
  page?: number;
  limit?: number;
  type?: string;
  tags?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'name' | 'size' | 'usage' | 'date';
  sortOrder?: 'asc' | 'desc';
}

export interface MediaSearchParams {
  q: string;
  type?: string;
  tags?: string;
}

export class MediaService {
  constructor(private config: ApiConfig) {}

  async uploadMedia(
    files: File[],
    altText?: string,
    tags?: string[],
    onProgress?: (progress: UploadProgress) => void
  ): Promise<MediaUploadResponse> {
    const formData = new FormData();
    
    // Add files
    files.forEach(file => {
      formData.append('media', file);
    });
    
    // Add metadata
    if (altText) {
      formData.append('altText', altText);
    }
    
    if (tags && tags.length > 0) {
      formData.append('tags', tags.join(','));
    }

    const xhr = new XMLHttpRequest();
    
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress({
            progress: (event.loaded / event.total) * 100,
            status: 'uploading'
          });
        }
      };
    }

    const uploadUrl = `${this.config.baseUrl}/media/upload`;
    
    return new Promise((resolve, reject) => {
      
      // Get token directly from localStorage as fallback
      const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      const token = authStorage.state?.token;
      
      xhr.open('POST', uploadUrl);
      
      // Add headers from config, skipping Content-Type (FormData sets it) and Authorization (set fresh below)
      Object.entries(this.config.headers).forEach(([key, value]) => {
        if (key !== 'Content-Type' && key !== 'Authorization') {
          xhr.setRequestHeader(key, value);
        }
      });

      // Set Authorization header once with the freshest token
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      } else {
        console.error('No token available for upload!');
      }

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          
          // Process uploaded media files to ensure correct URLs
          if (response.data && Array.isArray(response.data)) {
            response.data = response.data.map(processMediaFile);
          }
          
          if (onProgress) {
            onProgress({ progress: 100, status: 'complete' });
          }
          resolve(response);
        } else {
          if (onProgress) {
            onProgress({ progress: 0, status: 'error' });
          }
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        if (onProgress) {
          onProgress({ progress: 0, status: 'error' });
        }
        reject(new Error('Upload failed'));
      };

      xhr.send(formData);
    });
  }

  async getMedia(params: MediaListParams = {}): Promise<MediaListResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    // Ensure we have current auth token
    const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}');
    const token = authStorage.state?.token;
    const headers = {
      ...this.config.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };


    const response = await fetch(`${this.config.baseUrl}/media?${searchParams}`, {
      headers
    });

    const result = await handleResponse(response);
    
    // Process media files to ensure correct URLs
    if (result.data && Array.isArray(result.data)) {
      result.data = result.data.map(processMediaFile);
    }

    return result;
  }

  async getMediaById(id: string): Promise<{ success: boolean; data: MediaFile }> {
    const response = await fetch(`${this.config.baseUrl}/media/${id}`, {
      headers: this.config.headers
    });

    const result = await handleResponse(response);
    
    // Process media file to ensure correct URL
    if (result.data) {
      result.data = processMediaFile(result.data);
    }

    return result;
  }

  async updateMedia(id: string, data: { altText?: string; tags?: string[] }): Promise<{ success: boolean; message: string; data: MediaFile }> {
    const response = await fetch(`${this.config.baseUrl}/media/${id}`, {
      method: 'PUT',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    return handleResponse(response);
  }

  async deleteMedia(id: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.config.baseUrl}/media/${id}`, {
      method: 'DELETE',
      headers: this.config.headers
    });

    return handleResponse(response);
  }

  async searchMedia(params: MediaSearchParams): Promise<MediaSearchResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${this.config.baseUrl}/media/search?${searchParams}`, {
      headers: this.config.headers
    });

    const result = await handleResponse(response);
    
    // Process media files to ensure correct URLs
    if (result.data && Array.isArray(result.data)) {
      result.data = result.data.map(processMediaFile);
    }

    return result;
  }

  // Utility method to validate file before upload
  validateFile(file: File): string | null {
    const maxSize = 10 * 1024 * 1024; // 10MB per file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      return `File type ${file.type} is not supported. Allowed types: JPEG, PNG, WebP, GIF`;
    }

    if (file.size > maxSize) {
      return `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds the 10MB limit`;
    }

    return null;
  }

  // Utility method to format file size
  formatFileSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }
}